import { ApolloLink, Observable } from '@apollo/client';

// Times a query is sent again while the signed-in session keeps changing
export const MAX_RESENDS = 3;

export const SESSION_CHANGED_MESSAGE =
  'The signed-in user changed while the request was in flight';

const isMutation = operation =>
  operation.query.definitions.some(
    definition =>
      definition.kind === 'OperationDefinition' &&
      definition.operation === 'mutation'
  );

// The auth link adds the current user's token again, if there is one
const withoutAuthorization = ({ headers = {} }) => {
  const { authorization, Authorization, ...otherHeaders } = headers;

  return { headers: otherHeaders };
};

// A result is only delivered for the session it was requested for (CON-8904).
// A query whose session ended or changed while it was in flight is sent again
// for the current one. A mutation is never sent again, as it may already have
// taken effect; it fails instead.
const createSessionLink = getSessionKey =>
  new ApolloLink(
    (operation, forward) =>
      new Observable(observer => {
        const subscriptions = [];
        const latest = { attempt: 0 };

        const send = attempt => {
          latest.attempt = attempt;
          const isLatest = () => latest.attempt === attempt;
          const sessionAtSend = getSessionKey();

          // Errors are checked like results: a failure of the previous session
          // must not reach the current one
          const settle = deliver => {
            if (!isLatest()) {
              return;
            }

            if (getSessionKey() === sessionAtSend) {
              deliver();
              return;
            }

            if (isMutation(operation) || attempt >= MAX_RESENDS) {
              observer.error(new Error(SESSION_CHANGED_MESSAGE));
              return;
            }

            operation.setContext(withoutAuthorization);
            send(attempt + 1);
          };

          subscriptions.push(
            forward(operation).subscribe({
              next: result => settle(() => observer.next(result)),
              error: error => settle(() => observer.error(error)),
              complete: () => {
                if (isLatest()) {
                  observer.complete();
                }
              },
            })
          );
        };

        send(0);

        return () => {
          subscriptions.forEach(subscription => subscription.unsubscribe());
        };
      })
  );

export default createSessionLink;
